import {
  PLAN_VERSION_STATUSES,
  PRODUCTS,
  isProductKey,
  type PlanVersionSummary,
  type ProductKey,
} from '@traceio-org/platform-contracts';

/** A product that is billed: every product but Trace Admin (billedProductKeySchema). */
export type BilledProductKey = PlanVersionSummary['productKey'];

/** The billed products, in the contract's order of PRODUCTS. */
export const BILLED_PRODUCTS: readonly BilledProductKey[] = (Object.keys(PRODUCTS) as ProductKey[]).filter(
  (key): key is BilledProductKey => key !== 'admin',
);

export function isBilledProductKey(value: string | null): value is BilledProductKey {
  return value !== null && isProductKey(value) && value !== 'admin';
}

export type PlanVersionStatus = PlanVersionSummary['status'];

export function isPlanVersionStatus(value: string | null): value is PlanVersionStatus {
  return value !== null && (PLAN_VERSION_STATUSES as readonly string[]).includes(value);
}

/** The address of a plan version's page. */
export function planVersionPath(versionId: string): string {
  return `/billing/plan-versions/${encodeURIComponent(versionId)}`;
}

/** Organization and plan version ids are UUIDs (platform-api answers 400 to anything else). */
export const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The plan key platform-api accepts: lowercase letters, digits and hyphens, 1 to 64 characters, no edge hyphen. */
export const PLAN_KEY = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;

/** A plan as the list of versions describes it: its versions, newest first. */
export interface PlanOfVersions {
  planId: string;
  planKey: string;
  planName: string;
  planKind: PlanVersionSummary['planKind'];
  productKey: BilledProductKey;
  organizationId: string | null;
  versions: PlanVersionSummary[];
}

/** The plans behind a list of versions (the list is newest first; each plan keeps that order). */
export function plansOf(versions: readonly PlanVersionSummary[]): PlanOfVersions[] {
  const plans = new Map<string, PlanOfVersions>();
  for (const version of versions) {
    const plan = plans.get(version.planId) ?? {
      planId: version.planId,
      planKey: version.planKey,
      planName: version.planName,
      planKind: version.planKind,
      productKey: version.productKey,
      organizationId: version.organizationId,
      versions: [],
    };
    plan.versions.push(version);
    plans.set(version.planId, plan);
  }
  for (const plan of plans.values()) plan.versions.sort((a, b) => b.version - a.version);
  return [...plans.values()].sort((a, b) => a.planName.localeCompare(b.planName));
}
